import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";

import "../../../style/orderPrint.css";
import { getOrderDetail } from "../../../services/orders-api";

const DEFAULT_ORDER = {
  orderNumber: "ORD-1024",
  createdAt: "2025-11-05T09:30:00Z",
  customerName: "Anthony William Anderson",
  totalAmount: 2340.0,
  items: [],
};

const formatCurrency = (value) => `€${(Number(value) || 0).toFixed(2)}`;

const OrderViewPrint = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const hasPrintedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const { isPartsURL, isAccessoriesURL } = useAppSelector(
    (state) => state.domain
  );
  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        setLoading(true);
        const data = await getOrderDetail(id);
        setOrderData(data);
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrderDetail();
    }
  }, [id]);

  const order = orderData || DEFAULT_ORDER;

  const products = order.items || [];

  useEffect(() => {
    if (!hasPrintedRef.current && orderData && !loading) {
      const timer = setTimeout(() => {
        window.print();
        hasPrintedRef.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [orderData, loading]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="order-print-container">
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="order-print-container">
        <div className="text-center p-5">
          <h3>Order not found</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="order-print-container">
      <div className="order-print-header">
        <button
          className={` ${
            isPartsURL
              ? "order-print-back-btn-parts"
              : isAccessoriesURL
              ? "order-print-back-btn-acc"
              : "order-print-back-btn"
          }`}
          onClick={handleBack}
        >
          <ArrowLeft size={15} />
          Back to Order
        </button>
        <button
          className={` ${
            isPartsURL
              ? "order-print-print-btn-parts"
              : isAccessoriesURL
              ? "order-print-print-btn-acc"
              : "order-print-print-btn"
          }`}
          onClick={handlePrint}
        >
          <Printer size={16} />
          Print
        </button>
      </div>

      <div
        className={`${
          isPartsURL
            ? "order-print-content-parts"
            : isAccessoriesURL
            ? "order-print-content-acc"
            : "order-print-content"
        }`}
      >
        <div className="order-print-info-name">
          <div className="order-print-customer-name">{order.customer_name}</div>

          <div className="order-print-customer-info">
            {order.items_count} Items | {order.order_code} |{" "}
            {formatCurrency(order.total_amount)}
          </div>
        </div>

        <div className="order-print-order-table">
          <div className="order-print-table-header">
            <div className="order-print-col-product">PRODUCT</div>
            <div className="order-print-col-product">PRICE</div>
            <div className="order-print-col-qty">QTY.</div>
            <div className="order-print-col-total">TOTAL</div>
          </div>

          <div className="order-print-table-body">
            {products.map((item) => (
              <div
                key={item.id}
                className={`order-print-table-row ${
                  item.isOutOfStock ? "order-print-out-of-stock-row" : ""
                }`}
              >
                <div className="order-print-col-product">
                  <div className="order-print-product-info">
                    {item.product_detail?.image ? (
                      <img
                        src={item.product_detail.image}
                        alt={item.product_detail?.name}
                        className=""
                      />
                    ) : (
                      <div className="order-product-placeholder">
                        <span className="order-placeholder-text">
                          {item.product_detail?.name || "Product"}
                        </span>
                      </div>
                    )}
                    <div className="order-print-product-details">
                      <p className="order-print-product-name">
                        {item.product_detail?.name}
                        {item.product_detail?.suffix &&
                          ` ${item.product_detail.suffix}`}
                        <br />
                        <span className="order-product-specs">
                          {[
                            item.product_detail?.ram,
                            item.product_detail?.storage,
                            item.product_detail?.variants?.join(", "),
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </span>
                      </p>
                      {item.product_detail && (
                        <span
                          className={`${
                            isPartsURL
                              ? "order-print-condition-badge-parts"
                              : isAccessoriesURL
                              ? "order-print-condition-badge-acc"
                              : "order-print-condition-badge"
                          } ${
                            item.product_detail?.used_mobile ? "used" : "new"
                          }`}
                        >
                          {item.product_detail?.used_mobile ? "Used" : "New"}
                        </span>
                      )}
                      {item.isOutOfStock && (
                        <div className="order-print-out-of-stock-info">
                          <p className="order-print-not-included-text">
                            <span className="order-print-removed-from-inventory">
                              Removed from inventory
                            </span>
                            Not included in total.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="order-print-col-price-value">
                  {formatCurrency(item.unit_price)}
                </div>
                <div className="order-print-col-qty-value">
                  <span className="order-print-qty-value">{item.quantity}</span>
                </div>
                <div className="order-print-col-total-value">
                  {formatCurrency(item.line_total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderViewPrint;
